import { Button, Markdown } from "@procurement-agents/ui";
import { outcomesOf, serialiseReviewReply, splitAssumption, unanswered, type AssumptionResponse, type AttachmentMeta, type AttachmentRef, type FactResponse, type Placeholder, type QuestionResponse, type ReplyWithAttachments, type RunViewSme } from "@procurement-agents/scope-api";
import { useRef, useState, type ReactNode } from "react";
import { ACCEPT, AttachmentPicker, TurnAttachments, type Uploader } from "../renderers/Attachments";
import { NeededCards, type NeededActions } from "../renderers/Needed";
import { NoteField, ResponseRow } from "../renderers/ResponseRow";
import { toConfirm } from "../placeholders";

/** The SME clarification card (CHANGE_review_responses). Three kinds of item, kept apart and answered one by one:
 *  the questions, the assumptions (each with what it affects) and the needed facts ([TO CONFIRM] items and QA
 *  findings procurement sent here). Ordered so the first question is on a phone's first screen. Also used by
 *  procurement ("Answer as procurement"). `agent` renders the editing agent under a disclosure; once a proposal is
 *  accepted the reply carries EDITED so regeneration is skipped and the SME's wording stands. */
export function SmeCard({ view, onSubmit, busy, asProcurement, upload, onOpenAttachment, agent, updatedBy, finishing = 0, facts }: { finishing?: number; facts?: { items: Placeholder[]; actions?: NeededActions }; view: RunViewSme; onSubmit: (value: string | ReplyWithAttachments) => void; busy: boolean; asProcurement?: boolean; upload?: Uploader; onOpenAttachment?: (a: AttachmentRef) => void; agent?: (onAccepted: () => void) => ReactNode; updatedBy?: string | null }) {
  const { section, context, next } = view;
  const questions = section.sme_questions?.length ? section.sme_questions : next.questions?.length ? next.questions : next.question ? [next.question] : [];
  const purpose = section.purpose ?? next.purpose ?? null;
  const items = (facts?.items ?? []).filter((p) => p.section_id === section.section_id);
  const [openAtStart] = useState(() => new Set(items.filter((p) => p.status === "open").map((p) => p.id)));   // what this card is asked to settle
  const [assumptions, setAssumptions] = useState<AssumptionResponse[]>(() => section.assumptions.filter((a) => !a.includes("[TO CONFIRM")).map((text) => ({ text, response: null })));
  const [answers, setAnswers] = useState<QuestionResponse[]>(() => questions.map(() => ({ answer: "", unknown: false })));
  const [factNotes, setFactNotes] = useState<Record<string, { note: string; unknown: boolean }>>({});
  const [factFiles, setFactFiles] = useState<Record<string, AttachmentMeta>>({});   // a document that says the fact, sent with the reply
  const [files, setFiles] = useState<AttachmentMeta[]>([]);
  const [questionFiles, setQuestionFiles] = useState<AttachmentMeta[][]>(() => questions.map(() => []));
  const [additional, setAdditional] = useState("");
  const [edited, setEdited] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [confirm, setConfirm] = useState<"unanswered" | "finish" | null>(null);
  const venues = context.reference_row_names;

  const factResponses = (allUnknown = false): FactResponse[] => [...openAtStart].map((id) => {
    const p = items.find((x) => x.id === id);
    const local = factNotes[id];
    const file = factFiles[id];
    const response = p?.status === "filled" || file ? "provided" : p?.status === "dismissed" ? "not_needed" : (allUnknown || local?.unknown) ? "unknown" : null;
    return { id, response, note: local?.note, attachmentId: p?.status === "open" ? file?.attachment_id : undefined };
  });
  const pending = unanswered(assumptions, answers, factResponses());
  const send = (allUnknown = false) => {
    const a = allUnknown ? assumptions.map((x) => ({ ...x, response: x.response ?? "unknown" as const })) : assumptions;
    const q = allUnknown ? answers.map((x) => (x.answer.trim() ? x : { ...x, unknown: true })) : answers;
    const text = serialiseReviewReply(a, q, factResponses(allUnknown), additional, edited);
    const ids = [...questionFiles.flat(), ...Object.values(factFiles), ...files].map((f) => f.attachment_id);
    onSubmit(ids.length ? { text, attachment_ids: ids } : text);
  };
  // unanswered items are confirmed once; answering the last section as procurement finishes the scope, said first too
  const submit = () => {
    if (pending > 0 && confirm !== "unanswered" && confirm !== "finish") return setConfirm("unanswered");
    if (finishing > 0 && confirm !== "finish") return setConfirm("finish");
    send();
  };
  const setA = (i: number, patch: Partial<AssumptionResponse>) => setAssumptions((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setQ = (i: number, patch: Partial<QuestionResponse>) => setAnswers((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setF = (id: string, patch: Partial<{ note: string; unknown: boolean }>) => setFactNotes((m) => ({ ...m, [id]: { note: "", unknown: false, ...m[id], ...patch } }));
  if (view.status === "running") return <p className="muted center">Regenerating…</p>;
  const req = view.requisition;
  return (
    <article className="sme-card">
      <header className="context">
        <p className="small muted">{[context.tenant_display_name, context.title, venues.length ? venues.join(", ") : null].filter(Boolean).join(" · ")}</p>
        <details className="small">
          <summary>Show the request</summary>
          {(req?.business_need ?? context.business_need) && <p>{req?.business_need ?? context.business_need}</p>}
          {(req?.scope_summary ?? context.scope_summary) && <p className="muted">{req?.scope_summary ?? context.scope_summary}</p>}
          {req && outcomesOf(req).length > 0 && <ul>{outcomesOf(req).map((o) => <li key={o.id}>{o.text}</li>)}</ul>}
        </details>
      </header>
      <h2 className="section-name">{section.title}{view.needs_sme_count > 1 ? <span className="muted small"> · {view.needs_sme_count - 1} more queued</span> : null}</h2>
      {purpose && <p className="purpose">{purpose}</p>}

      {questions.length > 0 && (
        <section className="questions" aria-label="Questions">
          {questions.map((q, i) => (
            <div key={i} className="sme-item question-item">
              <p className="question"><span className="muted small">Question {i + 1}</span><br />{q}</p>
              {!answers[i]!.unknown && (upload
                ? <AttachmentPicker upload={upload} items={questionFiles[i] ?? []} onChange={(f) => setQuestionFiles((xs) => xs.map((x, j) => (j === i ? f : x)))} disabled={busy}>
                    <textarea rows={2} value={answers[i]!.answer} onChange={(e) => setQ(i, { answer: e.target.value })} placeholder="Your answer" aria-label={`Answer to question ${i + 1}`} />
                  </AttachmentPicker>
                : <textarea rows={2} value={answers[i]!.answer} onChange={(e) => setQ(i, { answer: e.target.value })} placeholder="Your answer" aria-label={`Answer to question ${i + 1}`} />)}
              <ResponseRow label={`Question ${i + 1}`} options={[{ id: "unknown", label: "? Not my area" }]} value={answers[i]!.unknown ? "unknown" : null} onChange={(v) => setQ(i, { unknown: v === "unknown" })} />
            </div>
          ))}
        </section>
      )}

      {assumptions.length > 0 && (
        <section className="assumptions" aria-label="Assumptions">
          <h3>What the agent assumed</h3>
          <ul>
            {assumptions.map((a, i) => {
              const { statement, affects } = splitAssumption(a.text);
              return (
                <li key={i} className={`sme-item ${a.response ?? ""}`}>
                  <p>{statement}{affects && <span className="muted small affects"> {affects}</span>}</p>
                  <ResponseRow label={`Assumption: ${statement}`} options={[{ id: "confirmed", label: "✓ That's right" }, { id: "corrected", label: "✎ Not quite…" }, { id: "unknown", label: "? Not my area" }]}
                    value={a.response} onChange={(v) => setA(i, { response: v as AssumptionResponse["response"] })} />
                  {a.response === "corrected" && <textarea rows={2} placeholder="What is correct?" value={a.correction ?? ""} onChange={(e) => setA(i, { correction: e.target.value })} aria-label={`Correction for: ${statement}`} autoFocus />}
                  <NoteField about={statement} value={a.note ?? ""} onChange={(note) => setA(i, { note })} />
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {items.length > 0 && (
        <section className="needed-facts" aria-label="Needed facts">
          <NeededCards items={items} actions={facts?.actions} busy={busy} extra={(p) => (
            <div className="fact-responses">
              {upload && p.status === "open" && p.kind !== "reference" && !factNotes[p.id]?.unknown
                && <FactDocument need={p.need} file={factFiles[p.id]} busy={busy} upload={(f) => upload(f, p.id)} onChange={(f) => setFactFiles(({ [p.id]: _, ...rest }) => (f ? { ...rest, [p.id]: f } : rest))} />}
              <ResponseRow label={`Respond to: ${p.need}`} options={[{ id: "unknown", label: "? Not my area", disabled: !!factFiles[p.id] }]} value={factNotes[p.id]?.unknown ? "unknown" : null} onChange={(v) => setF(p.id, { unknown: v === "unknown" })} />
              <NoteField about={p.need} value={factNotes[p.id]?.note ?? ""} onChange={(note) => setF(p.id, { note })} />
            </div>
          )} />
        </section>
      )}
      {(view.attachments?.length ?? 0) > 0 && <p className="small muted">Already attached: <TurnAttachments items={view.attachments} onOpen={onOpenAttachment} /></p>}

      <details open={edited || undefined}>
        <summary>Show the section the agent wrote{edited ? " (with your edits)" : ""}</summary>
        {updatedBy && <p className="small muted status-line">updated by {updatedBy} just now</p>}
        <Markdown text={section.content} />
      </details>
      {agent && (
        <details className="sme-agent" open={agentOpen} onToggle={(e) => setAgentOpen((e.target as HTMLDetailsElement).open)}>
          <summary>Edit the wording with the agent</summary>
          {agentOpen && agent(() => setEdited(true))}
          {edited && <p className="small muted">Your accepted changes are already in the section; Send records them with your answers.</p>}
        </details>
      )}

      {upload ? (
        <AttachmentPicker upload={upload} items={files} onChange={setFiles} disabled={busy}>
          <label className="field">
            <span className="field-label">Anything else?</span>
            <textarea rows={3} value={additional} onChange={(e) => setAdditional(e.target.value)} placeholder={`Anything about ${section.title.replace(/^[\d.]+\s*/, "").toLowerCase()} the tender should know?`} aria-label="Anything else" />
          </label>
        </AttachmentPicker>
      ) : (
        <label className="field">
          <span className="field-label">Anything else?</span>
          <textarea rows={3} value={additional} onChange={(e) => setAdditional(e.target.value)} placeholder={`Anything about ${section.title.replace(/^[\d.]+\s*/, "").toLowerCase()} the tender should know?`} aria-label="Anything else" />
        </label>
      )}
      <footer className="sme-actions">
        {confirm === "unanswered" && pending > 0
          ? <span className="row" role="alert"><span className="small">{pending} item{pending === 1 ? "" : "s"} unanswered — send anyway?</span><Button variant="primary" disabled={busy} onClick={() => (finishing > 0 ? setConfirm("finish") : send())}>Send anyway</Button><Button variant="quiet" onClick={() => setConfirm(null)}>Keep answering</Button></span>
          : confirm === "finish"
            ? <span className="row"><span className="small">This is the last section: answering finishes the scope with {toConfirm(finishing)}. Finish anyway?</span><Button variant="primary" disabled={busy} onClick={() => send()}>Yes, finish</Button><Button variant="quiet" onClick={() => setConfirm(null)}>Cancel</Button></span>
            : <Button variant="primary" disabled={busy} onClick={submit}>{asProcurement ? "Answer as procurement" : "Send"}</Button>}
        {!asProcurement && <button type="button" className="link" disabled={busy} onClick={() => send(true)} title="None of this is yours to answer: every item is passed back to procurement">Not my area</button>}
      </footer>
    </article>
  );
}

/** The document that says a needed fact (CHANGE_review_responses follow-up): uploaded for that item, sent with the
 *  reply as "PROVIDED: placeholder <id> attachment <aid>"; the rewrite after Send fills the item from it, or leaves it
 *  open with the document for procurement if it does not say it. */
function FactDocument({ need, file, busy, upload, onChange }: { need: string; file?: AttachmentMeta; busy: boolean; upload: (f: File) => Promise<AttachmentMeta>; onChange: (f: AttachmentMeta | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<{ uploading?: boolean; error?: string }>({});
  const add = async (f: File) => {
    setState({ uploading: true });
    try { onChange(await upload(f)); setState({}); } catch (e) { setState({ error: `${f.name}: ${(e as Error).message}` }); }
  };
  if (file) return (
    <p className="small fact-document">
      <span className={`chip removable attachment ${file.extracted_ok ? "" : "warn"}`}>{file.name}{file.extracted_ok ? "" : " · no readable text"}
        <button type="button" className="link" onClick={() => onChange(null)} aria-label={`Remove ${file.name}`} disabled={busy}>✕</button></span>
      <span className="muted"> The agent fills it in from this when you send.</span>
    </p>
  );
  return (
    <span className="row">
      <button type="button" className="link small" onClick={() => input.current?.click()} disabled={busy || state.uploading}>{state.uploading ? "Uploading…" : "📎 Attach the document that says it"}</button>
      <input ref={input} type="file" accept={ACCEPT} hidden aria-label={`Document for ${need}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void add(f); }} />
      {state.error && <span className="small warn-text" role="alert">{state.error}</span>}
    </span>
  );
}
